<template lang="pug">
  .support-settings(v-loading="loading")
    .support-page-header
      h1 Soporte · Configuración
      .support-page-actions
        el-button(type="primary" :loading="saving" @click="save") 💾 Guardar

    el-row(:gutter="16")
      el-col(:span="12")
        el-card
          h3 Asistente
          el-form(label-width="180px" label-position="left" size="small")
            el-form-item(label="Habilitado")
              el-switch(v-model="form.enabled")
            el-form-item(label="API key OpenAI")
              el-input(
                v-model="apiKeyInput"
                type="password"
                show-password
                :placeholder="hasApiKey ? '•••• cargada (escribí para reemplazar)' : 'sk-...'"
              )
              .support-hint(v-if="hasApiKey") API key actualmente cargada. Para reemplazarla, escribí una nueva.
              .support-hint(v-else) Necesaria para que el bot responda. Sin esta key el chat no funciona.
            el-form-item(label="Modelo")
              el-select(v-model="form.assistant.model")
                el-option(label="gpt-4o-mini (default)" value="gpt-4o-mini")
                el-option(label="gpt-4o (mejor razonamiento)" value="gpt-4o")
            el-form-item(label="Umbral de confianza")
              el-slider(v-model="confidenceThresholdPct" :min="0" :max="100" :step="5" show-input)
            el-form-item(label="Max mensajes por conversación")
              el-input-number(v-model="form.assistant.maxMessagesPerConversation" :min="1" :max="200")
            el-form-item(label="Max conversaciones por usuario/día")
              el-input-number(v-model="form.assistant.maxConversationsPerUserPerDay" :min="1" :max="100")
            el-form-item(label="System prompt (override)")
              el-input(type="textarea" v-model="form.assistant.systemPrompt" :rows="6" placeholder="Vacío = usar el default")

      el-col(:span="12")
        el-card
          h3 Notificaciones por email
          el-form(label-width="180px" label-position="left" size="small")
            el-form-item(label="Habilitado")
              el-switch(v-model="form.notifications.email.enabled")
            el-form-item(label="Destinatarios")
              el-input(v-model="emailRecipientsRaw" placeholder="op1@empresa.com, op2@empresa.com")
              .support-hint Separar por coma
            el-form-item(label="Solo escalaciones")
              el-switch(v-model="form.notifications.email.escalationOnly")
        el-card(style="margin-top: 16px")
          h3 Slack (V2 — placeholder)
          p.support-hint Pendiente para V2. El webhook se configurará desde acá cuando esté disponible.
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportSettings
 * @description Configuración del módulo de soporte por org (Fase 6 —
 *              UC-REG-098). Edita assistant config + notificaciones email.
 *              Slack queda placeholder para V2.
 * @example <SupportSettings />
 */
export default {
  name: "SupportSettings",
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        enabled: false,
        assistant: {
          provider: "openai",
          model: "gpt-4o-mini",
          confidenceThreshold: 0.7,
          maxMessagesPerConversation: 30,
          maxConversationsPerUserPerDay: 20,
          systemPrompt: "",
        },
        notifications: {
          email: { enabled: false, recipients: [], escalationOnly: true },
        },
      },
      emailRecipientsRaw: "",
      /**
       * Input local del campo apiKey. NO se persiste en form.assistant.apiKey
       * (el backend NUNCA devuelve la apiKey real, solo `hasApiKey: bool`).
       * Si el operador escribe acá una key nueva, se envía en el PUT y el
       * backend la guarda. Si queda vacío, NO se envía → backend conserva
       * la actual.
       */
      apiKeyInput: "",
      /**
       * Indicador local de si el backend tiene una apiKey cargada.
       * Viene del GET como `assistant.hasApiKey`. Se usa solo para mostrar
       * placeholder distinto en el input — nunca se envía al PUT.
       */
      hasApiKey: false,
    };
  },
  computed: {
    confidenceThresholdPct: {
      get() {
        return Math.round(
          (this.form.assistant.confidenceThreshold || 0.7) * 100
        );
      },
      set(v) {
        this.form.assistant.confidenceThreshold = v / 100;
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    /**
     * @description Carga config desde backend.
     */
    async load() {
      this.loading = true;
      try {
        const cfg = await supportService.getConfig();
        // Solo copio los campos que el form maneja — NO copio _id,
        // _virtual, organization, __v, createdAt, updatedAt (sino el PUT
        // los manda de vuelta al backend y rompe el upsert con campos
        // inmutables).
        if (typeof cfg.enabled === "boolean") {
          this.form.enabled = cfg.enabled;
        }
        if (cfg.assistant) {
          Object.assign(this.form.assistant, cfg.assistant);
          // hasApiKey es bool del backend — se guarda aparte, no en form.
          this.hasApiKey = !!cfg.assistant.hasApiKey;
          // Quitar del form.assistant para que no se mande de vuelta.
          delete this.form.assistant.hasApiKey;
        }
        if (cfg.notifications && cfg.notifications.email) {
          Object.assign(this.form.notifications.email, cfg.notifications.email);
          const r = cfg.notifications.email.recipients || [];
          this.emailRecipientsRaw = r.join(", ");
        }
      } catch (err) {
        this.$message.error("No pude cargar la config.");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Guarda config. Construye payload limpio (solo los
     *              campos del form, sin _id/_virtual/etc.) para evitar
     *              que el PUT rompa con campos inmutables. La apiKey
     *              solo se incluye si el operador escribió algo nuevo.
     */
    async save() {
      this.saving = true;
      try {
        const payload = {
          enabled: this.form.enabled,
          assistant: {
            provider: this.form.assistant.provider,
            model: this.form.assistant.model,
            systemPrompt: this.form.assistant.systemPrompt,
            confidenceThreshold: this.form.assistant.confidenceThreshold,
            maxMessagesPerConversation:
              this.form.assistant.maxMessagesPerConversation,
            maxConversationsPerUserPerDay:
              this.form.assistant.maxConversationsPerUserPerDay,
          },
          notifications: {
            email: {
              enabled: this.form.notifications.email.enabled,
              recipients: this.emailRecipientsRaw
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
              escalationOnly: this.form.notifications.email.escalationOnly,
            },
          },
        };
        // Solo incluir apiKey si el operador escribió algo (sino se
        // conserva la actual en backend).
        if (this.apiKeyInput && this.apiKeyInput.trim()) {
          payload.assistant.apiKey = this.apiKeyInput.trim();
        }
        const updated = await supportService.updateConfig(payload);
        // Refrescar hasApiKey con lo que devuelve el backend.
        if (updated && updated.assistant) {
          this.hasApiKey = !!updated.assistant.hasApiKey;
        }
        // Limpiar el input — si tenía valor, ya se guardó.
        this.apiKeyInput = "";
        this.$message.success("Configuración guardada.");
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          "No pude guardar.";
        this.$message.error(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.support-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  h1 {
    margin: 0;
    font-size: 22px;
    color: #002d5e;
  }
}
.support-hint {
  font-size: 11px;
  color: #6b7294;
}
</style>
